import { storage } from '@uhg-abyss/web/tools/storage';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { isEmpty } from 'lodash';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Constants, LOB, MemberLOB } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { LOADER_CENTER_STYLES } from '../../../../common/ConstantsStyles';
import {
  MOCK_MEMBERS_INFO,
  MOCK_STAGE_DEFAULT_MEMBER_INFO,
} from '../../../../common/MockMemberInfo';
import {
  fetchMpCodesFromOfferToken,
  getFeatureFlags,
} from '../../../../common/Utils';
import { dateGroupByYearMonthDay } from '../../../../common/Utils/datesUtils/formatDate';
import {
  isMaestroMNRMember,
  isNonMaestroMNRMember,
} from '../../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../../context/PortalContext';
import { getPlanSelectorVariables } from '../../../../frontends/ProviderSearch/context/PlanSelector/helpers';
import { useGetOfferRecommendations } from '../../../../hooks/useCeOffers';
import { useGetRecommendationPreferencesStatus } from '../../../../hooks/useGetRecommendationPreferencesStatus';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useOBAPI } from '../../../../hooks/useOBAPI';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { useCeOffersStore } from '../../../../store/useCeOffersStore';
import { useGuestStore } from '../../../../store/useGuestStore';
import { GuestState } from '../../../../store/useGuestStore/guestStore';
import { usePlanSelectorStore } from '../../../../store/usePlanSelectorStore';
import { useVirtualCareStore } from '../../../../store/useVirtualCareStore';
import { returnSuppressFlag } from '../../../../utils/featureSuppress';
import { loginNotRequired, stageEnv } from '../../../../utils/session.utils';
import {
  getCoverageReciprocityIdMap,
  getEnterpriseId,
  modifyRulesPackageKey,
} from '../../../../utils/user.utils';
import { setVirtualCareEligibilities } from '../../../../utils/virtualCare.utils';
import { IChildrenProp } from '../../types';

export const Obapi = ({ children, isWidget = false }: IChildrenProp) => {
  const { bigN } = useStore(StoreKeys.PLAN_SELECTOR);
  const { bigN: otherPlanBigN } = useStore(StoreKeys.OTHER_PLANS_SELECTOR);
  const { planToken } = getPlanSelectorVariables();
  const planContextIds = storage.cookie.get(Constants.COOKIES.PLAN_CONTEXT_IDS);
  const otherPlanTokens = planContextIds
    ?.split(':')
    ?.filter((token) => Boolean(token) && token !== planToken);
  const { setPlanInfo } = usePlanSelectorStore((state) => state);
  const { setEligibilities } = useVirtualCareStore((state) => state);
  const setMemberState = useStore(StoreKeys.SET_OBAPI_MEMBERS);
  const { setCeOffersData, clearCeOffersData } = useCeOffersStore(
    (state) => state
  );
  const isLocal = loginNotRequired();
  const allConfigs = useLagoon('config')();
  const hbpbCodes =
    allConfigs
      ?.find((row) => row.key === 'VISION_NON_UHC_ICARE')
      ?.value?.split(',') || [];
  const ceMaxOffers =
    allConfigs?.find((row) => row.key === 'CE_MAX_OFFERS')?.value || 100;

  const isStageEnv = stageEnv();

  const [members, setMembers] = useOBAPI({});
  const [offers, fetchOffers] = useGetOfferRecommendations({});
  const [otherPlanMembers, setOtherPlanMembers] = useOBAPI({});

  const [preferencesData] = useGetRecommendationPreferencesStatus({});
  const isLoadingPreferences = preferencesData?.isLoading;
  const isLoadingMemberData = members?.isLoading;
  const isLoading = isLoadingMemberData || isLoadingPreferences;
  const tierBenefits = useLagoon('tier-benefit')();
  const featureFlags: [{ key: string; active: boolean }] = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_FLAGS
  )();

  const [
    requestReciprocityId,
    enableMnrCosmos,
    enableCns,
    showIsPreEffectiveIndicator,
    extractProductCodeMarketTypeForUspNonOx,
    policyFilterToggle,
    enableVisionForNonUhcMNR,
    isVisionChipEnabled,
    isDentalChipEnabled,
    useNewReferralsIndicator,
    enableGroupNumberBasedRouting,
    enableMemberInfoCache,
    isHcpVirtualCareEnabled,
    enablePlanTypeSelectionInFilter,
    enableVirtualCareEpic,
    enableProgramPromotion,
    isSuppressPreEffectiveFF,
    enableNonGatedPcpSuppression,
    enableReferralCspaAuthz,
  ] = getFeatureFlags(featureFlags, [
    ConstantsLagoon.FEATURE_FLAGS.REQUEST_RECIPROCITY_ID,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MNR_COSMOS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CNS,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_ISPREEFFECTIVE_INDICATOR,
    ConstantsLagoon.FEATURE_FLAGS
      .ENABLE_EXTRACT_PRODUCT_CODE_MARKET_TYPE_FOR_USP_NON_OXFORD,
    ConstantsLagoon.FEATURE_FLAGS.MEMBER_INFO_POLICY_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VISION_FOR_NON_UHC_MNR,
    ConstantsLagoon.FEATURE_FLAGS.VISION_CHIP_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.DENTAL_CHIP_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_REFERRALS_INDICATOR,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_GROUP_NUMBER_BASED_ROUTING,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MEMBER_INFO_CACHE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_HCP_VIRTUAL_CARE_ELIGIBILITY,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLAN_TYPE_SELECTION_IN_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PROGRAM_PROMOTION,
    ConstantsLagoon.FEATURE_FLAGS.IS_SUPPRESS_PREEFFECTIVE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NON_GATED_PCP_SUPPRESSION,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_REFERRAL_CSPA_AUTHZ,
  ]);

  const suppressVisionCoverage = !isVisionChipEnabled;
  const suppressDentalCoverage = !isDentalChipEnabled;

  const { guestData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestData: state.guestData,
    }))
  );

  const { DEMO_MEMBER_INFO, USE_PRE_PROD, PORTAL_SOURCE } =
    Constants.STORAGE_KEYS.SESSION;
  const sessionStoragePortalSource = storage.session.get(PORTAL_SOURCE) || {};

  const [enablePreProd] = useState(storage.session.get(USE_PRE_PROD, false));
  useEffect(() => {
    storage.session.set(USE_PRE_PROD, enablePreProd);
  }, [enablePreProd]);

  const {
    firstName,
    lastName,
    groupNumber,
    memberId,
    dob,
    id,
    userName,
    portalSource,
    lob,
    usePreProd,
  } = storage.session.get(DEMO_MEMBER_INFO) || {};

  const fpcPsxRedirection = useLagoon(
    Constants.LAGOON_TABLE.FPC_PSX_REDIRECTION
  )();
  const eligiblePolicyIds =
    fpcPsxRedirection?.[0]?.eligiblePolicyNumbers?.split(',') || [];

  const eligibleGroupIds =
    fpcPsxRedirection?.[0]?.groupNumber?.split(',') || [];

  const { portalData } = useContext(PortalContext);
  const delsysCode = portalData.portalEligibilityData?.delsysCode;
  const enablePcp = portalData.portalEligibilityData?.enablePcp;
  const isUserMNRMaestroMember = isMaestroMNRMember(
    lob,
    sessionStoragePortalSource
  );

  useEffect(() => {
    const offersData = offers?.data?.getOfferRecommendations?.map((offer) => ({
      ...offer,
      MPCode: fetchMpCodesFromOfferToken(offer?.offerToken),
    }));
    setCeOffersData(offersData || []);
  }, [offers?.data?.getOfferRecommendations]);

  useEffect(() => {
    if (
      !isLocal &&
      enablePlanTypeSelectionInFilter &&
      (otherPlanBigN?.MemberNumber?.length || otherPlanBigN?.userName?.length)
    ) {
      otherPlanTokens.forEach((token) => {
        setOtherPlanMembers({
          variables: {
            firstName: otherPlanBigN.FirstName,
            lastName: otherPlanBigN.LastName,
            groupNumber: otherPlanBigN.GroupNumber,
            memberId: otherPlanBigN.MemberNumber,
            dob: otherPlanBigN.DOB
              ? dateGroupByYearMonthDay(otherPlanBigN.DOB)
              : undefined,
            id: otherPlanBigN.id,
            userName: otherPlanBigN.userName,
            portalSource: otherPlanBigN.portalSource,
            lob: '',
            requestReciprocityId,
            eligiblePolicyIds,
            eligibleGroupIds,
            enableGroupNumberBasedRouting,
            usePreProd: enablePreProd,
            enableMnrCosmos,
            enableCns,
            showIsPreEffectiveIndicator,
            extractProductCodeMarketTypeForUspNonOx,
            enableVirtualCareEligibilities: enableVirtualCareEpic,
            policyFilterToggle,
            enableVisionForNonUhcMNR,
            useNewReferralsIndicator,
            suppressVisionCoverage,
            suppressDentalCoverage,
            ...(isHcpVirtualCareEnabled && {
              token,
            }),
            visionNonUhcHPBPCodes: hbpbCodes,
            enableNonGatedPcpSuppression,
          },
        });
      });
    }
  }, [otherPlanBigN]);

  useEffect(() => {
    if (userName || memberId || portalData.portalEligibilityData) {
      const portalSourceValue =
        portalSource ||
        portalData?.portalEligibilityData?.portalSource ||
        portalData?.portalName;

      setMembers({
        variables: {
          ...((lob === LOB.ENI ||
            lob === LOB.CNS ||
            lob === LOB.IFP ||
            portalData.portalEligibilityData ||
            isUserMNRMaestroMember) && {
            firstName: portalData.portalEligibilityData?.firstName || firstName,
            lastName: portalData.portalEligibilityData?.lastName || lastName,
            ...(isUserMNRMaestroMember
              ? {} // Do not include groupNumber if isMaestroMNRMember is true, OBAPI in memberInfo expects big4
              : {
                  groupNumber:
                    portalData.portalEligibilityData?.policyId || groupNumber,
                }),
            memberId: portalData.portalEligibilityData?.memberId || memberId,
            dob: portalData.portalEligibilityData?.dob || dob,
          }),
          ...(lob === LOB.MNR &&
            isNonMaestroMNRMember(lob, sessionStoragePortalSource) && {
              id,
              userName,
              portalSourceValue,
            }),
          ...(enableVirtualCareEpic && { userName }),
          lob,
          usePreProd:
            usePreProd || portalData?.portalEligibilityData?.useObapiPreprod,
          enableMnrCosmos,
          enableCns,
          requestReciprocityId,
          eligiblePolicyIds,
          eligibleGroupIds,
          enableGroupNumberBasedRouting,
          showIsPreEffectiveIndicator,
          extractProductCodeMarketTypeForUspNonOx,
          enableVirtualCareEligibilities: enableVirtualCareEpic,
          policyFilterToggle,
          enableVisionForNonUhcMNR,
          useNewReferralsIndicator,
          ...(portalData.portalEligibilityData && {
            portalEligibilityData: {
              delsysCode,
              enablePcp,
              portalIdentifier: portalData.portalIdentifier,
            },
          }),
          portalSource: portalSourceValue,
          suppressVisionCoverage,
          suppressDentalCoverage,
          enableMemberInfoCache,
          ...(isHcpVirtualCareEnabled && {
            planToken,
          }),
          visionNonUhcHPBPCodes: hbpbCodes,
          enableNonGatedPcpSuppression,
          enableReferralCspaAuthz,
        },
      });
    } else if (
      !isLocal &&
      (bigN?.MemberNumber?.length || bigN?.userName?.length)
    ) {
      setMembers({
        variables: {
          firstName: bigN.FirstName,
          lastName: bigN.LastName,
          groupNumber: bigN.GroupNumber,
          memberId: bigN.MemberNumber,
          dob: bigN.DOB ? dateGroupByYearMonthDay(bigN.DOB) : undefined,
          id: bigN.id,
          userName: bigN.userName,
          portalSource: bigN.portalSource,
          lob: '',
          requestReciprocityId,
          eligiblePolicyIds,
          eligibleGroupIds,
          enableGroupNumberBasedRouting,
          usePreProd: enablePreProd,
          enableMnrCosmos,
          enableCns,
          showIsPreEffectiveIndicator,
          extractProductCodeMarketTypeForUspNonOx,
          enableVirtualCareEligibilities: enableVirtualCareEpic,
          policyFilterToggle,
          enableVisionForNonUhcMNR,
          useNewReferralsIndicator,
          suppressVisionCoverage,
          suppressDentalCoverage,
          enableMemberInfoCache,
          ...(isHcpVirtualCareEnabled && {
            planToken,
          }),
          visionNonUhcHPBPCodes: hbpbCodes,
          enableNonGatedPcpSuppression,
          enableReferralCspaAuthz,
        },
      });
    }
  }, [bigN]);

  useEffect(() => {
    if (planToken && members?.data) {
      setPlanInfo(
        planToken,
        getCoverageReciprocityIdMap(members?.data?.memberInfo[0], featureFlags),
        members?.data?.memberInfo[0]?.planDetail?.planName,
        members?.data?.memberInfo[0]?.population
      );
    }
  }, [members?.data]);

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const isVirtualCareSuppressedForEmpire = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_EMPIRE
  );
  const isPreEffective =
    members?.data?.memberInfo[0]?.isPreEffective && !isSuppressPreEffectiveFF;

  useEffect(() => {
    if (otherPlanTokens && otherPlanMembers?.data) {
      otherPlanTokens.forEach((token) => {
        setPlanInfo(
          token,
          getCoverageReciprocityIdMap(
            otherPlanMembers?.data?.memberInfo[0],
            featureFlags
          ),
          otherPlanMembers?.data?.memberInfo[0]?.planDetail?.planName,
          otherPlanMembers?.data?.memberInfo[0]?.population
        );
      });
    }
  }, [otherPlanMembers?.data]);

  useEffect(() => {
    if (!isEmpty(members?.data?.memberInfo[0])) {
      const enterpriseId = getEnterpriseId(members?.data?.memberInfo[0]);

      if (enableProgramPromotion && enterpriseId && !isPreEffective) {
        clearCeOffersData();
        fetchOffers({
          variables: {
            getOfferRecommendationsData: {
              enterpriseId,
              lineOfBusiness:
                MemberLOB[members?.data?.memberInfo[0]?.lineOfBusiness] ||
                members?.data?.memberInfo[0]?.lineOfBusiness ||
                '',
              maxOffers: parseInt(ceMaxOffers),
              contentSearchFilters: {
                categoryContext: Constants.CE_OFFERS_CATEGORY_CONTEXT,
              },
            },
          },
        });
      }
      setVirtualCareEligibilities(
        enableVirtualCareEpic,
        planToken,
        members,
        isVirtualCareUnetRailroadSuppressed,
        isVirtualCareSuppressedForEmpire,
        setEligibilities
      );
    }
    return () => {
      if (!isEmpty(members?.data?.memberInfo[0])) {
        clearCeOffersData();
        setEligibilities(planToken, []); // Clear eligibilities on unmount
      }
    };
  }, [
    members?.data,
    enableProgramPromotion,
    isPreEffective,
    isVirtualCareUnetRailroadSuppressed,
    isVirtualCareSuppressedForEmpire,
  ]);

  useEffect(() => {
    if (!isEmpty(otherPlanMembers?.data?.memberInfo[0])) {
      setVirtualCareEligibilities(
        enableVirtualCareEpic,
        otherPlanTokens?.join(','),
        otherPlanMembers,
        isVirtualCareUnetRailroadSuppressed,
        isVirtualCareSuppressedForEmpire,
        setEligibilities
      );
    }

    return () => {
      if (!isEmpty(otherPlanMembers?.data?.memberInfo[0])) {
        setEligibilities(otherPlanTokens?.join(','), []); // Clear eligibilities on unmount
      }
    };
  }, [
    otherPlanMembers?.data,
    isVirtualCareUnetRailroadSuppressed,
    isVirtualCareSuppressedForEmpire,
  ]);

  useEffect(() => {
    if (members.data) {
      const memberInfo = modifyRulesPackageKey(
        members.data.memberInfo,
        tierBenefits
      );

      setMemberState(memberInfo);
    }
    if (isStageEnv && !members.data) {
      setMemberState(MOCK_STAGE_DEFAULT_MEMBER_INFO);
    } else if (isLocal && !members.data) {
      if (!guestData?.ndbLob) {
        setMemberState(MOCK_MEMBERS_INFO);
      }
    }
  }, [members]);

  // Widget skeleton defined at lower component, full app uses spinner
  if (isLoading) {
    if (isWidget) {
      return null;
    }
    return (
      <div style={LOADER_CENTER_STYLES}>
        <LoadingSpinner ariaLoadingLabel="Loading page" isLoading={isLoading} />
      </div>
    );
  }

  return <Fragment>{children}</Fragment>;
};
